import {filter, cloneDeep} from 'lodash';
import {ActionTypes} from '../actions';
import {Dealer} from '../../common/actionTypes';
import createReducer from '../../common/util/createReducer';

const INITIAL_STATE = {
  'saving': false,
  'saved': false,
  'loading': false,
  'autosave': false,
  'deleting': false,
  'deleted': false,
  'id': '',
  'formNumber': '',
  'formId': '',
  'name': '',
  'section': '',
  'type': '',
  'status': '',
  'glossary': {},
  'sections': [],
  'answers': {},
  'chassisInfo': {},
  'generalInfo': {},
  'attachments': [],
};

const getActiveSections = (sections) => {
  const activeSections = filter(sections, {'isActive': true});

  return activeSections.map((section) => {
    delete section.isActive;

    let activeFields = filter(section.fields, {'isActive': true});
    activeFields = activeFields.map((field) => {
      delete field.isActive;

      if (field.type === 'text' || field.type === 'checkbox') {
        let activeOptions = filter(field.options, {'isActive': true});
        activeOptions = activeOptions.map((option) => {
          delete option.isActive;
          return option;
        });
        field.options = activeOptions;
      }

      return field;
    });
    section.fields = activeFields;

    return section;
  });
};

const updateGeneralInformation = (generalInfo, {field, value}) => {
  const newInfo = {...generalInfo};
  newInfo[field] = value.toUpperCase();
  if (field === 'isRepared' && value === 'NO') {
    newInfo.repairOfDate = '';
  }
  return newInfo;
};

const updateAnswers = (answers, {fieldType, fieldId, optionId, value, isOther, isMultipleSelection}) => {
  const newAnswers = cloneDeep(answers);

  switch (fieldType) {
    default:
    case 'textArea':
      newAnswers[fieldId] = value;
      return newAnswers;
    case 'text':
    case 'number':
      if (isOther) {
        newAnswers[fieldId][`${optionId}-other`] = value;
        return newAnswers;
      }
      newAnswers[optionId] = value;
      return newAnswers;
    case 'checkbox':
      if (!isMultipleSelection) {
        newAnswers[fieldId] = {};
        newAnswers[fieldId][optionId] = value;
        return newAnswers;
      }

      if (!newAnswers.hasOwnProperty(fieldId)) {
        newAnswers[fieldId] = {};
      }
      if (value) {
        newAnswers[fieldId][optionId] = value;
        return newAnswers;
      }

      delete newAnswers[fieldId][optionId];
      if (isOther) {
        delete newAnswers[fieldId][`${optionId}-other`];
      }
      return newAnswers;
  }
};

const urltoFile = async(url, filename, mimeType) => {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new File([buf], filename, {'type': mimeType});
};

const convertBase64ToFiles = (attachments) => {
  const files = [];
  attachments.forEach((item) => {
    const base64 = `data:${item.mimetype};base64, ${item.file}`;
    urltoFile(base64, item.name, item.mimetype).then((file) => {
      files.push(file);
    });
  });
  return files;
};

export const templateFTR = (state, action) => {
  return createReducer(INITIAL_STATE, {
    [ActionTypes.CLEAR_FORM]: (state, action) => ({
      ...state,
      'saving': false,
      'saved': false,
      'loading': false,
      'autosave': false,
      'deleting': false,
      'deleted': false,
      'id': '',
      'formNumber': '',
      'formId': '',
      'name': '',
      'section': '',
      'type': '',
      'status': '',
      'glossary': {},
      'sections': [],
      'answers': {},
      'chassisInfo': {},
      'generalInfo': {},
      'attachments': [],
    }),
    [ActionTypes.FETCHING_FORM_TEMPLATE]: (state, action) => ({
      ...state,
      'loading': true,
    }),
    [ActionTypes.FETCHED_FORM_TEMPLATE]: (state, action) => {
      const formTemplate = action.response.data;
      return {
        ...state,
        'loading': false,
        'id': formTemplate._id.$oid,
        'name': formTemplate.name,
        'section': formTemplate.section,
        'status': formTemplate.status,
        'glossary': formTemplate.glossary,
        'sections': getActiveSections(formTemplate.sections),
      };
    },
    [ActionTypes.FETCHED_FORM_TEMPLATE_FAILED]: (state, action) => ({
      ...state,
      'loading': false,
      'id': '',
      'name': '',
      'section': '',
      'status': '',
      'glossary': {},
      'sections': [],
      'answers': {},
    }),
    [Dealer.FETCHED_DEALER]: (state, action) => {
      const dealerInfo = action.response.data;
      return {
        ...state,
        'generalInfo': {
          ...state.generalInfo,
          'dealerId': dealerInfo.identification,
          'dealerName': dealerInfo.name,
        },
      };
    },
    [ActionTypes.FETCHING_CHASSIS_INFO]: (state) => ({
      ...state,
      'loading': true,
    }),
    [ActionTypes.FETCHED_CHASSIS_INFO]: (state, action) => {
      const chassisData = action.response.data;
      return {
        ...state,
        'loading': false,
        'chassisInfo': chassisData,
        'generalInfo': {
          ...state.generalInfo,
          'modelName': chassisData.modelName,
        },
      };
    },
    [ActionTypes.FETCHED_CHASSIS_INFO_FAILED]: (state) => ({
      ...state,
      'loading': false,
      'chassisInfo': {},
    }),
    [ActionTypes.SET_GENERAL_INFO]: (state, action) => ({
      ...state,
      'generalInfo': updateGeneralInformation(state.generalInfo, action.payload),
    }),
    [ActionTypes.SET_VALUE_IN_FORM]: (state, action) => ({
      ...state,
      'answers': updateAnswers(state.answers, action.payload),
    }),
    [ActionTypes.SET_FILES]: (state, action) => ({
      ...state,
      'attachments': action.payload,
    }),
    [ActionTypes.SAVING_FORM]: (state) => ({
      ...state,
      'saving': true,
    }),
    [ActionTypes.SAVED_FORM]: (state, action) => {
      const formResponse = action.response.data;
      const autosave = action.payload;
      if (autosave) {
        return {
          ...state,
          'saving': false,
          'saved': true,
          'loading': false,
          'autosave': action.payload,
          'id': formResponse.answerId.$oid,
        };
      }
      return {
        ...state,
        'saving': false,
        'saved': true,
        'loading': false,
        'autosave': action.payload,
        'id': formResponse.answerId.$oid,
        'formNumber': '',
        'formId': '',
        'name': '',
        'section': '',
        'type': '',
        'status': '',
        'glossary': {},
        'sections': [],
        'answers': {},
        'chassisInfo': {},
        'generalInfo': {},
        'attachments': [],
      };
    },
    [ActionTypes.SAVED_FORM_FAILED]: (state) => ({
      ...state,
      'saving': false,
    }),
    [ActionTypes.FETCHING_FORM_RESPONSE]: (state, action) => ({
      ...state,
      'loading': true,
    }),
    [ActionTypes.FETCHED_FORM_RESPONSE]: (state, action) => {
      const formResponse = action.response.data;
      const actionType = action.payload;
      const files = convertBase64ToFiles(formResponse.attachments);

      return {
        ...state,
        'loading': false,
        'id': formResponse._id.$oid,
        'formNumber': formResponse.formNumber,
        'formId': formResponse.formId,
        'name': formResponse.name,
        'section': formResponse.section,
        'type': formResponse.type,
        'status': formResponse.status,
        'glossary': formResponse.glossary,
        'generalInfo': formResponse.generalInfo,
        'sections': formResponse.sections,
        'answers': formResponse.answers,
        'attachments': actionType === 'ver' ? formResponse.attachments : files,
        'attachmentsConvert': files,
        'attachmentsPure': formResponse.attachments,
      };
    },
    [ActionTypes.FETCHED_FORM_TEMPLATE_FAILED]: (state, action) => ({
      ...state,
      'loading': false,
      'id': '',
      'formNumber': '',
      'formId': '',
      'name': '',
      'section': '',
      'type': '',
      'status': '',
      'glossary': {},
      'generalInfo': '',
      'sections': [],
      'answers': {},
    }),
    [ActionTypes.FETCHING_ONLY_FORM_ANSWERS]: (state, action) => ({
      ...state,
      'loading': true,
    }),
    [ActionTypes.FETCHED_ONLY_FORM_ANSWERS]: (state, action) => {
      const formResponse = action.response.data;
      return {
        ...state,
        'loading': false,
        'answers': formResponse.answers,
      };
    },
    [ActionTypes.FETCHED_ONLY_FORM_ANSWERS_FAILED]: (state, action) => ({
      ...state,
      'loading': false,
      'answers': {},
    }),
    [ActionTypes.CHANGE_SAVED_VALUE]: (state, action) => ({
      ...state,
      'saved': false,
    }),
    [ActionTypes.DELETING_FORM]: (state, action) => ({
      ...state,
      'deleting': false,
      'deleted': false,
    }),
    [ActionTypes.DELETED_FORM]: (state, action) => ({
      ...state,
      'deleting': true,
      'deleted': true,
    }),
    [ActionTypes.DELETED_FORM_FAILED]: (state, action) => ({
      ...state,
      'deleting': true,
      'deleted': false,
    }),
  })(state, action);
};
